import { lazy, Suspense } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';

import { AppLayout } from '@/components/shell/AppLayout';
import { PageSkeleton } from '@/components/shell/PageSkeleton';
import { RouteErrorBoundary } from '@/errors/RouteErrorBoundary';

import { AdminPage } from './AdminPage';
import { AdminApisPage } from './admin/AdminApisPage';
import { AdminAuthProvidersPage } from './admin/AdminAuthProvidersPage';
import { AdminBuildInfoPage } from './admin/AdminBuildInfoPage';
import { AdminClusterPage } from './admin/AdminClusterPage';
import { AdminConfigsPage } from './admin/AdminConfigsPage';
import { AdminConsentTypesPage } from './admin/AdminConsentTypesPage';
import { AdminDatabasePage } from './admin/AdminDatabasePage';
import { AdminFederatedIdentitiesPage } from './admin/AdminFederatedIdentitiesPage';
import { AdminEmailPage } from './admin/AdminEmailPage';
import { AdminFhirIntegrationsListPage } from './admin/AdminFhirIntegrationsListPage';
import { AdminFhirIntegrationDetailPage } from './admin/AdminFhirIntegrationDetailPage';
import { AdminGeneralPage } from './admin/AdminGeneralPage';
import { AdminJobsPage } from './admin/AdminJobsPage';
import { AdminLogsPage } from './admin/AdminLogsPage';
import { AdminOidcCredentialsPage } from './admin/AdminOidcCredentialsPage';
import { AdminQuestionnairesPage } from './admin/AdminQuestionnairesPage';
import { AdminS3ConfigsPage } from '@/features/admin-s3-configs/AdminS3ConfigsPage';
import { AdminCarePathwaysPage } from './admin/AdminCarePathwaysPage';
import { AdminUserDetailPage } from './admin/AdminUserDetailPage';
import { AdminUsersPage } from './admin/AdminUsersPage';
import { AdminVocabulariesPage } from './admin/AdminVocabulariesPage';
import { BrowseFamiliesPage } from './BrowseFamiliesPage';
import { QuestionnairesPage } from './QuestionnairesPage';
import { BrowsePatientsPage } from './BrowsePatientsPage';
import { FamilyPage } from './FamilyPage';
import { HomeRedirect } from './HomeRedirect';
import { MyCasesPage } from './MyCasesPage';
import { NotFoundPage } from './NotFoundPage';
import { PatientPage } from './PatientPage';
import { PlatformPage } from './PlatformPage';
import { PlatformGrantsPage } from './platform/PlatformGrantsPage';
import { PlatformJobsPage } from './platform/PlatformJobsPage';
import { PlatformTemplatesPage } from './platform/PlatformTemplatesPage';
import { PlatformDockerHubPage } from './platform/PlatformDockerHubPage';
import { PlatformTenantsPage } from './platform/PlatformTenantsPage';
import { PlatformConfigsPage } from './platform/PlatformConfigsPage';
import { PlatformUsersPage } from './platform/PlatformUsersPage';
import { PlatformUserDetailPage } from './platform/PlatformUserDetailPage';
import { RequirePermission } from './RequirePermission';
import { TenantDetailView } from '@/features/platform/components/TenantDetailView';
import { TenantRoleDetailView } from '@/features/platform/components/TenantRoleDetailView';
import { AuthProviderDetailView } from '@/features/admin-auth/components/AuthProviderDetailView';

const PatientCreatePage = lazy(() =>
  import('./PatientCreatePage').then((m) => ({ default: m.PatientCreatePage })),
);

const FamilyCreatePage = lazy(() =>
  import('./FamilyCreatePage').then((m) => ({ default: m.FamilyCreatePage })),
);

const SelfUserPage = lazy(() =>
  import('./SelfUserPage').then((m) => ({ default: m.SelfUserPage })),
);

// Standalone (shell-less) patient-record PDF/print view — lazy so its section
// bundle only loads when someone exports (PTV2-1253).
const PatientRecordPrintPage = lazy(() =>
  import('./PatientRecordPrintPage').then((m) => ({ default: m.PatientRecordPrintPage })),
);

// Pedigree editor pages are lazy so the heavy @gene42/pedigree bundle (svg.js / d3)
// only loads when a pedigree route is opened.
const PatientPedigreePage = lazy(() =>
  import('./PatientPedigreePage').then((m) => ({ default: m.PatientPedigreePage })),
);

const FamilyPedigreePage = lazy(() =>
  import('./FamilyPedigreePage').then((m) => ({ default: m.FamilyPedigreePage })),
);

/**
 * Single source of truth for the app's route table. Exported as a plain
 * `RouteObject[]` so callers can mount with either `createBrowserRouter`
 * (production) or `createMemoryRouter` (tests) without duplicating the
 * shape.
 *
 * Keep this table flat-ish — nested routes only when they share both a
 * data dependency *and* a chrome layout. When in doubt, prefer composing
 * `PageWithSidebar` inside the page over adding a parent route.
 */
export const appRoutes: RouteObject[] = [
  // Shell-less print route — a sibling of AppLayout so no top nav / sidebar
  // renders into the PDF. Auth + query context still apply (both wrap the router).
  {
    path: 'patients/:guid/print',
    element: (
      <Suspense fallback={<PageSkeleton />}>
        <PatientRecordPrintPage />
      </Suspense>
    ),
  },
  {
    element: <AppLayout />,
    // Catch render errors inside the routed tree and route them through the
    // PHI-safe reportError → Sentry seam (data routers otherwise swallow them).
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <HomeRedirect /> },
      { path: 'my-cases', element: <MyCasesPage /> },
      { path: 'browse/patients', element: <BrowsePatientsPage /> },
      { path: 'browse/patients/mine', element: <BrowsePatientsPage /> },
      { path: 'browse/families', element: <BrowseFamiliesPage /> },
      { path: 'browse/questionnaires', element: <QuestionnairesPage /> },
      {
        path: 'patients/new',
        element: (
          <RequirePermission perm="createPatients">
            <Suspense fallback={<PageSkeleton />}>
              <PatientCreatePage />
            </Suspense>
          </RequirePermission>
        ),
      },
      { path: 'patients/:guid', element: <PatientPage /> },
      {
        path: 'patients/:guid/pedigree',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <PatientPedigreePage />
          </Suspense>
        ),
      },
      {
        path: 'profile',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <SelfUserPage />
          </Suspense>
        ),
      },
      {
        path: 'families/new',
        element: (
          <RequirePermission perm="createFamilies">
            <Suspense fallback={<PageSkeleton />}>
              <FamilyCreatePage />
            </Suspense>
          </RequirePermission>
        ),
      },
      { path: 'families/:guid', element: <FamilyPage /> },
      {
        path: 'families/:guid/pedigree',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <FamilyPedigreePage />
          </Suspense>
        ),
      },
      {
        path: 'admin',
        element: (
          <RequirePermission perm="openAdmin">
            <AdminPage />
          </RequirePermission>
        ),
        children: [
          { index: true, element: <Navigate to="/admin/settings" replace /> },
          { path: 'users', element: <AdminUsersPage /> },
          { path: 'consent-types', element: <AdminConsentTypesPage /> },
          { path: 'settings', element: <AdminGeneralPage /> },
          { path: 'configs', element: <AdminConfigsPage /> },
          { path: 'fhir-integrations', element: <AdminFhirIntegrationsListPage /> },
          { path: 'fhir-integrations/:guid', element: <AdminFhirIntegrationDetailPage /> },
          { path: 'apis', element: <AdminApisPage /> },
          { path: 'vocabularies', element: <AdminVocabulariesPage /> },
          { path: 'questionnaires', element: <AdminQuestionnairesPage /> },
          { path: 'email', element: <AdminEmailPage /> },
          { path: 's3-configs', element: <AdminS3ConfigsPage /> },
          // PTV2-1383: OAuth credentials + auth providers are global-plane
          // (cluster-wide) config — moved to the platform console below.
          { path: 'care-pathways', element: <AdminCarePathwaysPage /> },
          { path: 'jobs', element: <AdminJobsPage /> },
          // PTV2-1383: the Cluster tabs (Discovery/Build-info/Logs/Database) moved to the global
          // platform pane below — they are cluster-wide infra, not tenant admin.
        ],
      },
      {
        path: 'platform',
        element: (
          <RequirePermission perm="openPlatform">
            <PlatformPage />
          </RequirePermission>
        ),
        children: [
          { index: true, element: <Navigate to="/platform/tenants" replace /> },
          { path: 'tenants', element: <PlatformTenantsPage /> },
          { path: 'users', element: <PlatformUsersPage /> },
          { path: 'jobs', element: <PlatformJobsPage /> },
          // PTV2-1383: cluster-wide infra tabs, moved here from /admin so global platform admins
          // (platform:* but no admin:*) can reach them.
          { path: 'cluster', element: <AdminClusterPage /> },
          { path: 'build-info', element: <AdminBuildInfoPage /> },
          { path: 'logs', element: <AdminLogsPage /> },
          { path: 'database', element: <AdminDatabasePage /> },
          // PTV2-1383: global-plane OAuth/identity config (moved from /admin).
          {
            path: 'oidc-credentials',
            element: (
              <RequirePermission perm="managePlatform">
                <AdminOidcCredentialsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'auth-providers',
            element: (
              <RequirePermission perm="managePlatform">
                <AdminAuthProvidersPage />
              </RequirePermission>
            ),
          },
          {
            path: 'federated-identities',
            element: (
              <RequirePermission perm="managePlatform">
                <AdminFederatedIdentitiesPage />
              </RequirePermission>
            ),
          },
          {
            path: 'dockerhub',
            element: (
              <RequirePermission perm="managePlatform">
                <PlatformDockerHubPage />
              </RequirePermission>
            ),
          },
          {
            path: 'templates',
            element: (
              <RequirePermission perm="managePlatform">
                <PlatformTemplatesPage />
              </RequirePermission>
            ),
          },
          {
            path: 'grants',
            element: (
              <RequirePermission perm="managePlatform">
                <PlatformGrantsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'configs',
            element: (
              <RequirePermission perm="managePlatform">
                <PlatformConfigsPage />
              </RequirePermission>
            ),
          },
        ],
      },
      // Tenant detail view renders its own full-bleed layout (not the section
      // sidebar), so it sits as a sibling under the same permission gate.
      {
        path: 'platform/tenants/:uuid',
        element: (
          <RequirePermission perm="openPlatform">
            <TenantDetailView />
          </RequirePermission>
        ),
      },
      // First-class user pages (PTV2-1451). Like the tenant detail they render their own full-bleed
      // shell, so they are flat siblings (not nested under the section sidebar). Global: platform:MANAGE;
      // tenant: openAdmin. The self page (/profile) is accessible to any authenticated user.
      {
        path: 'platform/users/:uuid',
        element: (
          <RequirePermission perm="managePlatform">
            <PlatformUserDetailPage />
          </RequirePermission>
        ),
      },
      {
        path: 'admin/users/:uuid',
        element: (
          <RequirePermission perm="openAdmin">
            <AdminUserDetailPage />
          </RequirePermission>
        ),
      },
      // Role record page (PTV2-1444) — a full record view reached by clicking a role in a tenant's
      // Roles tab; permissions are edited here rather than in a modal.
      {
        path: 'platform/tenants/:uuid/roles/:guid',
        element: (
          <RequirePermission perm="openPlatform">
            <TenantRoleDetailView />
          </RequirePermission>
        ),
      },
      // Auth-provider detail page (PTV2-1383) — full record view (replaces the old edit modal),
      // sibling of the list under the same platform:MANAGE gate.
      {
        path: 'platform/auth-providers/:id',
        element: (
          <RequirePermission perm="managePlatform">
            <AuthProviderDetailView />
          </RequirePermission>
        ),
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];
